import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Building2, HandCoins, User } from "lucide-react";
import api from "@/lib/api";

export default function GlobalSearch({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const navigate = useNavigate();

  // ⌘K hotkey
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const [c, d, ct] = await Promise.all([
          api.get(`/clients`, { params: q ? { q } : {} }),
          api.get(`/deals`, { params: q ? { q } : {} }),
          api.get(`/contacts`, { params: q ? { q } : {} }),
        ]);
        setClients(c.data.slice(0, 6));
        setDeals(d.data.slice(0, 6));
        setContacts(ct.data.slice(0, 6));
      } catch (err) {
        console.error("Global search failed:", err);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    // shouldFilter={false} — results are already filtered SERVER-SIDE by the
    // /clients /deals /contacts `q` param. cmdk's built-in fuzzy filter was
    // re-ranking and hiding rows (e.g. a contact matched by phone/email was
    // hidden because its visible text didn't contain the query) and its
    // text-derived item values collided for same-named contacts, navigating
    // to the WRONG client. Explicit unique `value` per item fixes selection.
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <CommandInput
        placeholder="Search clients, deals, contacts…"
        value={q}
        onValueChange={setQ}
        data-testid="global-search-input"
      />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        {clients.length > 0 && (
          <CommandGroup heading="Clients">
            {clients.map((c) => (
              <CommandItem
                key={c.id}
                value={`client-${c.id}`}
                onSelect={() => go(`/clients/${c.id}`)}
                data-testid={`search-client-${c.id}`}
              >
                <Building2 className="h-4 w-4 mr-2 text-primary" />
                {c.company_name}
                {c.industry && <span className="ml-auto text-xs text-muted-foreground">{c.industry}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {deals.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Deals">
              {deals.map((d) => (
                <CommandItem
                  key={d.id}
                  value={`deal-${d.id}`}
                  onSelect={() => go(`/deals/${d.id}`)}
                  data-testid={`search-deal-${d.id}`}
                >
                  <HandCoins className="h-4 w-4 mr-2 text-primary" />
                  {d.title}
                  <span className="ml-auto text-xs text-muted-foreground capitalize">
                    {(d.stage || "").replace(/_/g, " ")}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {contacts.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Contacts">
              {contacts.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`contact-${c.id}`}
                  onSelect={() => go(c.client_id ? `/clients/${c.client_id}` : "/contacts")}
                  data-testid={`search-contact-${c.id}`}
                >
                  <User className="h-4 w-4 mr-2 text-primary" />
                  {c.name}
                  <span className="ml-auto text-xs text-muted-foreground">{c.company_name || c.email}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
